import { constructFrom, formatDistanceToNow } from 'date-fns';
import { toIsoDateTime } from '@kirin/api-client/helpers/date-time';
import {
  getAbsoluteTimeFormat,
  type AbsoluteTimeFormat,
} from '@/lib/absoluteTimeFormat';

/**
 * Accepted input shape for the FE-wide datetime helpers. ISO strings are
 * the wire format; `Date` is accepted for callers that already parsed; the
 * nullish/`unknown` cases are tolerated so consumers do not need to guard
 * around partial backend payloads (helpers return the supplied `fallback`
 * instead of throwing).
 */
export type DateInput = Date | string | number | null | undefined | unknown;

/**
 * Parse a backend-recorded instant into a valid `Date`, or `null` when it
 * cannot be parsed (NaN time after `new Date(value)`). The single parser behind
 * every formatter here, so date math and a rendered label can't disagree on a
 * value (e.g. a relative "… ago" label vs its absolute tooltip). Accepts epoch
 * millis as a number or a numeric string. Timezone-less datetime strings are
 * read as UTC, not the viewer's local time, and the Postgres text form parses
 * on every engine (see {@link toIsoDateTime}).
 */
export function coerceToUtcDate(value: DateInput): Date | null {
  if (value === null || value === undefined) return null;
  let d: Date;
  if (value instanceof Date) {
    d = value;
  } else if (typeof value === 'number') {
    d = new Date(value);
  } else if (typeof value === 'string') {
    const trimmed = value.trim();
    if (/^\d+$/.test(trimmed)) {
      d = new Date(parseInt(trimmed, 10));
    } else {
      d = new Date(toIsoDateTime(trimmed));
    }
  } else {
    return null;
  }
  return Number.isNaN(d.getTime()) ? null : d;
}

// Every rendered date + time carries an explicit zone label ("UTC", "EDT",
// "GMT+3", …). This is non-negotiable for backend-recorded events (audit
// logs, mute/exclude metadata, etc.): without it, viewers would silently
// misread the clock. The zone is the user's display preference
// (Settings → Preferences → Time Zone, UTC by default), and the date order and
// 12/24-hour clock are too (Timestamp Format → Absolute). Calendar-only dates
// come in two kinds: UTC calendar days (end-of-UTC-day expirations, OSV publish
// days) stay pinned to UTC ({@link formatUtcDate}) and say so whenever the
// display zone is not UTC, so the date never reads as local next to a zoned
// timestamp; event dates ("Connected on …") are the day in the display zone
// ({@link formatZonedDate}).
//
// Precision: we never show milliseconds. That precision is noise and makes
// the UX worse. Minute precision is enough in the vast majority of cases
// ({@link formatDateTimeToMinute}); seconds ({@link formatDateTime}) only
// where the most detailed activity view needs to tell events seconds apart.
const partsFormatters = new Map<string, Intl.DateTimeFormat>();

/** How much clock to render: none, to the minute, or to the second. */
type Precision = 'date' | 'minute' | 'second';

type Parts = Partial<Record<Intl.DateTimeFormatPartTypes, string>>;

/**
 * Date (and optionally time) fields of `d` in `timeZone`, by part type. The
 * month is spelled out (`long`, or `short` for chart axes) unless the date
 * order is numeric.
 */
function absoluteParts(
  d: Date,
  timeZone: string,
  { date, clock }: AbsoluteTimeFormat,
  precision: Precision,
  month: 'long' | 'short' = 'long'
): Parts {
  const key = [timeZone, date, clock, precision, month].join('|');
  let formatter = partsFormatters.get(key);
  if (!formatter) {
    const numeric = date === 'ymd';
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: numeric ? '2-digit' : month,
      day: numeric ? '2-digit' : 'numeric',
      ...(precision !== 'date' && {
        // h23 (not `hour12: false`) avoids the "24:00:00" midnight quirk.
        hourCycle: clock === '24h' ? 'h23' : 'h12',
        hour: clock === '24h' ? '2-digit' : 'numeric',
        minute: '2-digit',
        ...(precision === 'second' && { second: '2-digit' }),
        timeZoneName: 'short',
      }),
    });
    partsFormatters.set(key, formatter);
  }
  return Object.fromEntries(
    formatter.formatToParts(d).map(part => [part.type, part.value])
  );
}

/** A rendered absolute timestamp, split so a cell can put each on its own line. */
export interface DateTimeParts {
  /** "September 21, 2026" / "2026/09/21" / "21 September 2026". */
  date: string;
  /** "16:18:07 UTC" / "4:18:07 PM UTC"; '' for a date-only render. */
  time: string;
}

/** `p`'s date in the user's date order, with or without the year. */
function orderedDate(
  p: Parts,
  order: AbsoluteTimeFormat['date'],
  withYear = true
): string {
  if (order === 'ymd') {
    return withYear ? `${p.year}/${p.month}/${p.day}` : `${p.month}/${p.day}`;
  }
  if (order === 'dmy') {
    return withYear ? `${p.day} ${p.month} ${p.year}` : `${p.day} ${p.month}`;
  }
  return withYear ? `${p.month} ${p.day}, ${p.year}` : `${p.month} ${p.day}`;
}

/** `p`'s time of day on the user's clock, without the zone. */
function clockTime(
  p: Parts,
  clock: AbsoluteTimeFormat['clock'],
  precision: 'minute' | 'second'
): string {
  const hms =
    precision === 'second'
      ? `${p.hour}:${p.minute}:${p.second}`
      : `${p.hour}:${p.minute}`;
  return clock === '12h' ? `${hms} ${p.dayPeriod}` : hms;
}

function formatAbsolute(
  d: Date,
  timeZone: string,
  format: AbsoluteTimeFormat,
  precision: Precision
): DateTimeParts {
  const p = absoluteParts(d, timeZone, format, precision);
  const date = orderedDate(p, format.date);
  if (precision === 'date') return { date, time: '' };
  return {
    date,
    time: `${clockTime(p, format.clock, precision)} ${p.timeZoneName}`,
  };
}

/**
 * {@link formatDateTime} as its date and time halves, or `null` for nullish
 * or unparseable input.
 *
 * @param timeZone — As for {@link formatDateTime}.
 */
export function formatDateTimeParts(
  value: DateInput,
  timeZone: string
): DateTimeParts | null {
  const d = coerceToUtcDate(value);
  return d
    ? formatAbsolute(d, timeZone, getAbsoluteTimeFormat(), 'second')
    : null;
}

/**
 * Format a backend-recorded instant as a date + time in the user's display
 * time zone and absolute format, with the zone's short name appended (e.g.
 * `September 21, 2026 16:18:07 UTC`, the default). The default FE renderer
 * for any place a timestamp is surfaced.
 *
 * @param value — ISO string, `Date`, epoch millis, or nullish.
 * @param timeZone — Components pass `useDisplayTimeZone()` so they re-render
 *   on a change. Required so a caller cannot silently skip subscribing.
 * @param fallback — Returned for nullish or unparseable input. Default `'—'`.
 */
export function formatDateTime(
  value: DateInput,
  timeZone: string,
  fallback = '—'
): string {
  const parts = formatDateTimeParts(value, timeZone);
  return parts ? `${parts.date} ${parts.time}` : fallback;
}

/**
 * Compact chart-axis label in the user's formats: a day ("Sep 28", "28 Sep",
 * "09/28"), a day with its year ("Sep 28, 2026", "28 Sep 2026", "2026/09/28"),
 * just the year ("2026"), or a time of day ("14:00", "2:00 PM").
 */
export function formatChartTick(
  value: DateInput,
  timeZone: string,
  unit: 'day' | 'dayYear' | 'year' | 'time'
): string {
  const d = coerceToUtcDate(value);
  if (!d) return '';
  const format = getAbsoluteTimeFormat();
  if (unit === 'time') {
    const p = absoluteParts(d, timeZone, format, 'minute');
    return clockTime(p, format.clock, 'minute');
  }
  const p = absoluteParts(d, timeZone, format, 'date', 'short');
  if (unit === 'year') return p.year ?? '';
  return orderedDate(p, format.date, unit === 'dayYear');
}

/**
 * Just the date half of {@link formatDateTime}: the calendar day in the
 * user's display time zone and date format. For events whose time of day is
 * noise ("Connected on …"); end-of-day expirations use {@link formatUtcDate}.
 */
export function formatZonedDate(
  value: DateInput,
  timeZone: string,
  fallback = '—'
): string {
  const d = coerceToUtcDate(value);
  return d
    ? formatAbsolute(d, timeZone, getAbsoluteTimeFormat(), 'date').date
    : fallback;
}

/**
 * {@link formatDateTime} to the minute, with no seconds (e.g.
 * `September 21, 2026 16:18 UTC`). For values picked to the minute, like
 * date filter boundaries. A value with seconds or milliseconds is shown
 * truncated to its minute, never rounded.
 */
export function formatDateTimeToMinute(
  value: DateInput,
  timeZone: string,
  fallback = '—'
): string {
  const d = coerceToUtcDate(value);
  if (!d) return fallback;
  const { date, time } = formatAbsolute(
    d,
    timeZone,
    getAbsoluteTimeFormat(),
    'minute'
  );
  return `${date} ${time}`;
}

/**
 * Format a backend-recorded instant as a calendar date in UTC, in the user's
 * date format, no clock.
 *
 * @param labelUtc — Append " UTC". Callers pass `useDisplayTimeZone() !==
 *   'UTC'`, so the date never reads as local next to a zoned timestamp (see
 *   comment above).
 * @param fallback — Returned for nullish or unparseable input. Default `'—'`.
 */
export function formatUtcDate(
  value: DateInput,
  labelUtc: boolean,
  fallback = '—'
): string {
  const d = coerceToUtcDate(value);
  if (!d) return fallback;
  const { date } = formatAbsolute(d, 'UTC', getAbsoluteTimeFormat(), 'date');
  return labelUtc ? `${date} UTC` : date;
}

/**
 * Shared prologue for the relative helpers: {@link coerceToUtcDate}, except a
 * raw `number` is rejected (their callers have never passed epoch millis, and
 * several tests pin that contract).
 */
function parseRelativeDate(
  date: Date | string | undefined | null | unknown
): Date | null {
  return typeof date === 'number' ? null : coerceToUtcDate(date);
}

/**
 * Format a date as a relative time string with minute precision (the default
 * renderer for all screens whose timestamps are accurate to the minute).
 * - < 1 minute ago: "just now"
 * - 1–59 minutes ago: exact minutes (e.g. "1 minute ago", "37 minutes ago")
 * - 1–23 hours ago: date-fns "about X hour(s) ago"
 * - >= 1 day / future: date-fns ("5 days ago", "about 2 weeks ago", "in 2 hours")
 *
 * @param date - The date to format. Can be a Date object, ISO string, or other date-parseable value.
 * @returns A human-readable relative time string, or "invalid" if the date is invalid or missing
 */
export function formatRelativeTime(
  date: Date | string | undefined | null | unknown
): string {
  const dateObj = parseRelativeDate(date);
  if (!dateObj) {
    return 'invalid';
  }

  const now = new Date();
  const diffMs = now.getTime() - dateObj.getTime();

  // If the date is in the future (diffMs is negative) or 1 day or more in the past,
  // delegate to formatDistanceToNow which handles both future and distant past dates
  if (diffMs < 0 || diffMs >= 24 * 60 * 60 * 1000) {
    return formatDistanceToNow(dateObj, { addSuffix: true });
  }

  const diffMinutes = Math.floor(diffMs / (1000 * 60));

  // Less than 1 minute: "just now"
  if (diffMinutes < 1) {
    return 'just now';
  }

  // 1–59 minutes: exact minutes
  if (diffMinutes < 60) {
    return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
  }

  // 1–23 hours: lean on date-fns for the "about X hour(s) ago" wording
  return formatDistanceToNow(dateObj, { addSuffix: true });
}

/**
 * Fuzzy variant of {@link formatRelativeTime} for the one screen whose data is
 * not accurate to the minute (Inventory → Users → "Kirin Last Active"). Never
 * surfaces an exact minute count below the hour:
 * - < 15 minutes ago: "less than 15 minutes ago"
 * - 15–59 minutes ago: "less than 1 hour ago"
 * - 1–23 hours ago: date-fns "about X hour(s) ago"
 * - >= 1 day / future: same delegation as {@link formatRelativeTime}
 *
 * @param date - The date to format. Can be a Date object, ISO string, or other date-parseable value.
 * @returns A human-readable relative time string, or "invalid" if the date is invalid or missing
 */
export function formatRelativeTimeFuzzy(
  date: Date | string | undefined | null | unknown
): string {
  const dateObj = parseRelativeDate(date);
  if (!dateObj) {
    return 'invalid';
  }

  const now = new Date();
  const diffMs = now.getTime() - dateObj.getTime();

  // Future or 1 day or more in the past: identical delegation to the default.
  if (diffMs < 0 || diffMs >= 24 * 60 * 60 * 1000) {
    return formatDistanceToNow(dateObj, { addSuffix: true });
  }

  const diffMinutes = Math.floor(diffMs / (1000 * 60));

  // Less than 15 minutes: fuzzy lower bound.
  if (diffMinutes < 15) {
    return 'less than 15 minutes ago';
  }

  // 15–59 minutes: never an exact count, collapse to "less than 1 hour ago".
  if (diffMinutes < 60) {
    return 'less than 1 hour ago';
  }

  // 1–23 hours: same "about X hour(s) ago" wording as the default.
  return formatDistanceToNow(dateObj, { addSuffix: true });
}

/**
 * Elapsed-duration variant of {@link formatRelativeTime}: how long ago `date`
 * was, without the "ago" suffix. Used by columns that show a length of time
 * rather than a moment (Shadow AI → Running Now → "Running For").
 * - < 1 minute, or a future date (agent clock skew): "less than a minute"
 * - 1–59 minutes: exact minutes (e.g. "1 minute", "37 minutes")
 * - >= 1 hour: date-fns wording without suffix ("about 3 hours", "2 days")
 *
 * @param date - The date to format. Can be a Date object, ISO string, or other date-parseable value.
 * @returns A human-readable duration string, or "invalid" if the date is invalid or missing
 */
export function formatElapsedDuration(
  date: Date | string | undefined | null | unknown
): string {
  const dateObj = parseRelativeDate(date);
  if (!dateObj) {
    return 'invalid';
  }

  const diffMs = new Date().getTime() - dateObj.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));

  // Also covers future instants (negative delta): never show "in 5 minutes"
  // for something that is already running.
  if (diffMinutes < 1) {
    return 'less than a minute';
  }

  if (diffMinutes < 60) {
    return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'}`;
  }

  return formatDistanceToNow(dateObj);
}

/**
 * Date setters put a time in a zone's repeated (fall-back) hour on its first
 * occurrence, so editing 01:30 EST would give 01:45 EDT, an hour earlier.
 * Returns `next` moved to `reference`'s own UTC offset whenever it reads the
 * same clock there, else `next` as is. Both are read in their own zone (pass
 * `TZDate`s for a zone other than the browser's).
 */
export function keepOffset(reference: Date, next: Date): Date {
  const shifted = constructFrom(
    next,
    next.getTime() +
      (reference.getTimezoneOffset() - next.getTimezoneOffset()) * 60_000
  );
  return shifted.getHours() === next.getHours() &&
    shifted.getMinutes() === next.getMinutes()
    ? shifted
    : next;
}

/**
 * Returns the end of `d` (23:59:59.999) in UTC as an ISO 8601 string.
 *
 * Used to construct mute-alert expirations from a user-picked calendar day:
 * the user picks a day, and the mute is honored up to the very end of that
 * day in UTC. Always emits a Z-terminated ISO string so it round-trips
 * through Zod's `z.string().datetime()` validator on the wire.
 *
 * Date components are read in UTC to avoid local-timezone DST drift.
 *
 * @param d - Any Date instance (its time component is ignored)
 * @returns ISO 8601 datetime string at UTC end-of-day for `d`
 */
export function endOfDayUTC(d: Date): string {
  return new Date(
    Date.UTC(
      d.getUTCFullYear(),
      d.getUTCMonth(),
      d.getUTCDate(),
      23,
      59,
      59,
      999
    )
  ).toISOString();
}
